import {
  buildListQuery,
  DEFAULT_PAGE_SIZE,
  type PaginatedList,
  VIDEOS_PAGE_SIZE,
} from "./pagination";
import {
  clearSession,
  getAccessToken,
  getRefreshToken,
  persistSession,
} from "./auth-storage";
import type {
  AdminAuditEntry,
  AdminTokens,
  AdminUser,
  ApiErrorBody,
  Banner,
  BannerCategoryFilter,
  BannerOptions,
  BannerStatus,
  Badge,
  BadgeEarner,
  BadgeOptions,
  BadgeStats,
  BadgeStatus,
  CreateAdminInput,
  CreateBadgeInput,
  Blog,
  BlogListItem,
  BlogStatus,
  BlogStatusFilter,
  CreateBannerInput,
  CreateBlogInput,
  CreateProductInput,
  CreateVideoInput,
  LoginResponse,
  Member,
  MemberStatus,
  MemberStatusFilter,
  MemberSearchField,
  ReferredUser,
  WalletTypeFilter,
  WalletHistoryResult,
  Product,
  ProductOptions,
  ProductStats,
  ReferralLeaderboardEntry,
  ReferralRecord,
  ReferralStats,
  AdminSpLeaderboardResult,
  AdminKpLeaderboardRow,
  SpLeaderboardBoard,
  ConfigEntry,
  FeedbackSubmission,
  FeedbackSubmissionDetail,
  FeedbackAdminStatus,
  FeedbackAdminStatusFilter,
  FeedbackRequestTypeFilter,
  FeedbackTargetTypeFilter,
  DeviceRecord,
  DeviceStatus,
  DeviceStatusFilter,
  AskAiAskerTypeFilter,
  AskAiQuestion,
  AskAiQuestionDetail,
  AskAiQuestionStatusFilter,
  UpdateAdminInput,
  UpdateBadgeInput,
  UpdateBannerInput,
  UpdateBlogInput,
  UpdateMemberInput,
  UpdateProductInput,
  UpdateVideoMetadataInput,
  Video,
  VideoCreator,
  VideoStatus,
  VideoStatusFilter,
  Order,
  OrderDetail,
  OrderOptions,
  OrderStatus,
  OrderStatusFilter,
  OrderUpdatePayload,
  FulfilmentImportResult,
  FulfilmentStatusFilter,
  ChangeStatusResult,
  ProductBulkStatus,
  AdminStatus,
  FeedMatchCard,
  FeedMatchDetail,
  FeedMatchSummary,
  FeedMatchScorecard,
  FeedPlayerProfile,
  FeedPlayerSearchItem,
  FeedScheduleMatch,
  FeedTeamProfile,
  FeedSeries,
  FeedSeriesDetail,
  CreatePredictionInput,
  UpdatePredictionInput,
  DeclarePredictionResult,
  DeclarePredictionResultInput,
  DeletePredictionResult,
  VoidPredictionResult,
  PredictionDetail,
  PredictionEntryAdmin,
  PredictionEntryStatus,
  PredictionListItem,
  PredictionCategoryFilter,
  PredictionRecentActivity,
  PredictionStats,
  PredictionStatus,
} from "./types";

const API_URL = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8080";

export class ApiError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

async function parseError(res: Response): Promise<ApiError> {
  try {
    const body = (await res.json()) as ApiErrorBody;
    return new ApiError(body.message ?? "Request failed", res.status, body.data?.code);
  } catch {
    return new ApiError(res.statusText || "Request failed", res.status);
  }
}

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  const res = await fetch(`${API_URL}/v1/admin/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });

  if (!res.ok) {
    clearSession();
    return null;
  }

  const tokens = (await res.json()) as AdminTokens;
  const admin = getStoredAdminFromRefresh();
  if (admin) {
    persistSession(
      tokens.accessToken,
      tokens.refreshToken,
      admin,
      tokens.expiresIn,
    );
  } else {
    localStorage.setItem("sporush_admin_access", tokens.accessToken);
    localStorage.setItem("sporush_admin_refresh", tokens.refreshToken);
  }
  return tokens.accessToken;
}

function getStoredAdminFromRefresh(): AdminUser | null {
  const raw = localStorage.getItem("sporush_admin_user");
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AdminUser;
  } catch {
    return null;
  }
}

async function authorizedFetch(
  path: string,
  init: RequestInit = {},
  retry = true,
): Promise<Response> {
  const headers = new Headers(init.headers);
  const isFormData = typeof FormData !== "undefined" && init.body instanceof FormData;
  if (!headers.has("Content-Type") && init.body && !isFormData) {
    headers.set("Content-Type", "application/json");
  }

  const token = getAccessToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(`${API_URL}${path}`, { ...init, headers });

  if (res.status === 401 && retry) {
    if (!refreshPromise) {
      refreshPromise = refreshAccessToken().finally(() => {
        refreshPromise = null;
      });
    }
    const newToken = await refreshPromise;
    if (newToken) return authorizedFetch(path, init, false);
    throw new ApiError("Session expired — please sign in again", 401, "unauthorized");
  }

  return res;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authorizedFetch(path, init);
  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// --- Auth ---

export async function login(email: string, password: string): Promise<LoginResponse> {
  const res = await fetch(`${API_URL}/v1/admin/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) throw await parseError(res);
  return (await res.json()) as LoginResponse;
}

export async function logout(allSessions = false): Promise<void> {
  try {
    await request<void>("/v1/admin/auth/logout", {
      method: "POST",
      body: JSON.stringify({ allSessions }),
    });
  } finally {
    clearSession();
  }
}

export async function fetchMe(): Promise<AdminUser> {
  return request<AdminUser>("/v1/admin/auth/me");
}

// --- Admins (super-admin) ---

export async function listAdmins(
  pageNumber = 1,
  pageSize = DEFAULT_PAGE_SIZE,
): Promise<PaginatedList<AdminUser>> {
  return request<PaginatedList<AdminUser>>(
    `/v1/admin/users${buildListQuery(pageNumber, pageSize)}`,
  );
}

export async function listAdminAudit(
  pageNumber = 1,
  pageSize = DEFAULT_PAGE_SIZE,
): Promise<PaginatedList<AdminAuditEntry>> {
  return request<PaginatedList<AdminAuditEntry>>(
    `/v1/admin/users/audit${buildListQuery(pageNumber, pageSize)}`,
  );
}

export async function createAdmin(payload: CreateAdminInput): Promise<AdminUser> {
  return request<AdminUser>("/v1/admin/users", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateAdmin(id: string, payload: UpdateAdminInput): Promise<AdminUser> {
  return request<AdminUser>(`/v1/admin/users/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function deactivateAdmin(id: string): Promise<void> {
  await changeAdminStatus([id], "blocked");
}

export async function deleteAdmin(id: string): Promise<void> {
  await request<void>(`/v1/admin/users/${id}`, { method: "DELETE" });
}

// --- Banners ---

export async function getBannerOptions(): Promise<BannerOptions> {
  return request<BannerOptions>("/v1/admin/banners/options");
}

export async function listBanners(
  options: {
    category?: BannerCategoryFilter;
    status?: BannerStatus;
    pageNumber?: number;
    pageSize?: number;
  } = {},
): Promise<PaginatedList<Banner>> {
  const { category = "all", status, pageNumber = 1, pageSize = DEFAULT_PAGE_SIZE } = options;
  return request<PaginatedList<Banner>>(
    `/v1/admin/banners${buildListQuery(pageNumber, pageSize, {
      category: category === "all" ? undefined : category,
      status,
    })}`,
  );
}

export async function createBanner(payload: CreateBannerInput): Promise<Banner> {
  return request<Banner>("/v1/admin/banners", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function getBanner(id: string): Promise<Banner> {
  return request<Banner>(`/v1/admin/banners/${id}`);
}

export async function updateBanner(id: string, payload: UpdateBannerInput): Promise<Banner> {
  return request<Banner>(`/v1/admin/banners/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function uploadBannerImage(file: File): Promise<{ imageUrl: string }> {
  const form = new FormData();
  form.append("image", file);
  return request<{ imageUrl: string }>("/v1/admin/banners/upload-image", {
    method: "POST",
    body: form,
  });
}

export async function setBannerStatus(id: string, status: BannerStatus): Promise<void> {
  await changeBannerStatus([id], status);
}

export async function changeBannerStatus(
  ids: string[],
  status: BannerStatus,
): Promise<ChangeStatusResult> {
  return patchChangeStatus("/v1/admin/banners/change-status", ids, status);
}

// --- Blogs ---

export async function listBlogs(
  options: {
    status?: BlogStatusFilter;
    search?: string;
    pageNumber?: number;
    pageSize?: number;
  } = {},
): Promise<PaginatedList<BlogListItem>> {
  const { status = "all", search, pageNumber = 1, pageSize = DEFAULT_PAGE_SIZE } = options;
  return request<PaginatedList<BlogListItem>>(
    `/v1/admin/blogs${buildListQuery(pageNumber, pageSize, {
      status: status === "all" ? undefined : status,
      search: search?.trim() || undefined,
    })}`,
  );
}

export async function createBlog(payload: CreateBlogInput): Promise<Blog> {
  return request<Blog>("/v1/admin/blogs", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function getBlog(id: string): Promise<Blog> {
  return request<Blog>(`/v1/admin/blogs/${id}`);
}

export async function updateBlog(id: string, payload: UpdateBlogInput): Promise<Blog> {
  return request<Blog>(`/v1/admin/blogs/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function uploadBlogImage(file: File): Promise<{ imageUrl: string }> {
  const form = new FormData();
  form.append("image", file);
  return request<{ imageUrl: string }>("/v1/admin/blogs/upload-image", {
    method: "POST",
    body: form,
  });
}

export async function improveBlog(payload: {
  title?: string | null;
  content: string;
  prompt?: string | null;
}): Promise<{ content: string }> {
  return request<{ content: string }>("/v1/admin/blogs/improve", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function changeBlogStatus(
  ids: string[],
  status: BlogStatus,
): Promise<ChangeStatusResult> {
  return patchChangeStatus("/v1/admin/blogs/change-status", ids, status);
}

// --- Badges ---

export async function getBadgeOptions(): Promise<BadgeOptions> {
  return request<BadgeOptions>("/v1/admin/badges/options");
}

export async function getBadgeStats(): Promise<BadgeStats> {
  return request<BadgeStats>("/v1/admin/badges/stats");
}

export async function listBadges(
  options: {
    search?: string;
    status?: BadgeStatus;
    kpRange?: string;
    pageNumber?: number;
    pageSize?: number;
  } = {},
): Promise<PaginatedList<Badge>> {
  const {
    search,
    status,
    kpRange,
    pageNumber = 1,
    pageSize = DEFAULT_PAGE_SIZE,
  } = options;
  return request<PaginatedList<Badge>>(
    `/v1/admin/badges${buildListQuery(pageNumber, pageSize, {
      search,
      status,
      kpRange,
    })}`,
  );
}

export async function createBadge(payload: CreateBadgeInput): Promise<Badge> {
  return request<Badge>("/v1/admin/badges", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function getBadge(id: string): Promise<Badge> {
  return request<Badge>(`/v1/admin/badges/${id}`);
}

export async function updateBadge(id: string, payload: UpdateBadgeInput): Promise<Badge> {
  return request<Badge>(`/v1/admin/badges/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function uploadBadgeImage(file: File): Promise<{ imageUrl: string }> {
  const form = new FormData();
  form.append("image", file);
  return request<{ imageUrl: string }>("/v1/admin/badges/upload-image", {
    method: "POST",
    body: form,
  });
}

export async function changeBadgeStatus(
  ids: string[],
  status: BadgeStatus,
): Promise<ChangeStatusResult> {
  return patchChangeStatus("/v1/admin/badges/change-status", ids, status);
}

export async function listBadgeEarners(
  id: string,
  options: { search?: string; pageNumber?: number; pageSize?: number } = {},
): Promise<PaginatedList<BadgeEarner>> {
  const { search, pageNumber = 1, pageSize = DEFAULT_PAGE_SIZE } = options;
  return request<PaginatedList<BadgeEarner>>(
    `/v1/admin/badges/${id}/earners${buildListQuery(pageNumber, pageSize, {
      search,
    })}`,
  );
}

// --- Videos ---

export async function listVideoCreators(
  options: { search?: string; pageNumber?: number; pageSize?: number } = {},
): Promise<PaginatedList<VideoCreator>> {
  const { search, pageNumber = 1, pageSize = 100 } = options;
  return request<PaginatedList<VideoCreator>>(
    `/v1/admin/videos/creators${buildListQuery(pageNumber, pageSize, { search })}`,
  );
}

export async function listVideos(
  options: {
    status?: VideoStatusFilter;
    mode?: "default" | "trending";
    includeDisabled?: boolean;
    pageNumber?: number;
    pageSize?: number;
  } = {},
): Promise<PaginatedList<Video>> {
  const {
    status = "all",
    mode = "default",
    includeDisabled = true,
    pageNumber = 1,
    pageSize = VIDEOS_PAGE_SIZE,
  } = options;
  return request<PaginatedList<Video>>(
    `/v1/admin/videos${buildListQuery(pageNumber, pageSize, {
      status,
      mode,
      includeDisabled,
    })}`,
  );
}

export async function createVideo(payload: CreateVideoInput): Promise<Video> {
  const form = new FormData();
  form.append("title", payload.title);
  if (payload.description) form.append("description", payload.description);
  form.append("status", payload.status ?? "draft");
  form.append("sortOrder", String(payload.sortOrder ?? 0));
  if (payload.durationSeconds != null) {
    form.append("durationSeconds", String(payload.durationSeconds));
  }
  form.append("meta", JSON.stringify(payload.meta ?? {}));
  if (payload.creatorUserId) {
    form.append("creatorUserId", payload.creatorUserId);
  }
  form.append("video", payload.video);
  form.append("thumbnail", payload.thumbnail);

  return request<Video>("/v1/admin/videos", {
    method: "POST",
    body: form,
  });
}

export async function setVideoStatus(id: string, status: VideoStatus): Promise<Video> {
  return request<Video>(`/v1/admin/videos/${id}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
}

export async function updateVideoMetadata(
  id: string,
  payload: UpdateVideoMetadataInput,
): Promise<Video> {
  return request<Video>(`/v1/admin/videos/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function deleteVideo(id: string): Promise<void> {
  return request<void>(`/v1/admin/videos/${id}`, { method: "DELETE" });
}

// --- Marketplace ---

export async function getProductOptions(): Promise<ProductOptions> {
  return request<ProductOptions>("/v1/admin/marketplace/options");
}

export async function getProductStats(): Promise<ProductStats> {
  return request<ProductStats>("/v1/admin/marketplace/stats");
}

export async function listProducts(
  options: {
    category?: string;
    status?: "all" | "active" | "inactive";
    search?: string;
    pageNumber?: number;
    pageSize?: number;
  } = {},
): Promise<PaginatedList<Product>> {
  const {
    category,
    status = "all",
    search,
    pageNumber = 1,
    pageSize = DEFAULT_PAGE_SIZE,
  } = options;
  return request<PaginatedList<Product>>(
    `/v1/admin/marketplace/products${buildListQuery(pageNumber, pageSize, {
      category: category === "all" ? undefined : category,
      status: status === "all" ? undefined : status,
      search: search?.trim() || undefined,
    })}`,
  );
}

export async function getProduct(id: string): Promise<Product> {
  return request<Product>(`/v1/admin/marketplace/products/${id}`);
}

export async function createProduct(payload: CreateProductInput): Promise<Product> {
  return request<Product>("/v1/admin/marketplace/products", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateProduct(id: string, payload: UpdateProductInput): Promise<Product> {
  return request<Product>(`/v1/admin/marketplace/products/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function deleteProduct(id: string): Promise<void> {
  return request<void>(`/v1/admin/marketplace/products/${id}`, { method: "DELETE" });
}

export async function uploadProductImage(file: File): Promise<{ imageUrl: string }> {
  const form = new FormData();
  form.append("image", file);
  return request<{ imageUrl: string }>("/v1/admin/marketplace/upload-image", {
    method: "POST",
    body: form,
  });
}

export async function changeProductStatus(
  ids: string[],
  status: ProductBulkStatus,
): Promise<ChangeStatusResult> {
  return patchChangeStatus("/v1/admin/marketplace/products/change-status", ids, status);
}

// --- Orders ---

export async function getOrderOptions(): Promise<OrderOptions> {
  return request<OrderOptions>("/v1/admin/orders/options");
}

export async function listOrders(
  options: {
    status?: OrderStatusFilter;
    productId?: string;
    userId?: string;
    category?: string;
    dateFrom?: string;
    dateTo?: string;
    pageNumber?: number;
    pageSize?: number;
  } = {},
): Promise<PaginatedList<Order>> {
  const {
    status = "active",
    productId,
    userId,
    category,
    dateFrom,
    dateTo,
    pageNumber = 1,
    pageSize = DEFAULT_PAGE_SIZE,
  } = options;
  return request<PaginatedList<Order>>(
    `/v1/admin/orders${buildListQuery(pageNumber, pageSize, {
      status,
      productId,
      userId,
      category: category === "all" ? undefined : category,
      dateFrom,
      dateTo,
    })}`,
  );
}

export async function getOrder(id: string): Promise<OrderDetail> {
  return request<OrderDetail>(`/v1/admin/orders/${id}`);
}

export async function updateOrder(
  id: string,
  payload: OrderUpdatePayload,
): Promise<OrderDetail> {
  return request<OrderDetail>(`/v1/admin/orders/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function changeOrderStatus(
  ids: string[],
  status: OrderStatus,
): Promise<ChangeStatusResult> {
  return patchChangeStatus("/v1/admin/orders/change-status", ids, status);
}

// --- Manual fulfilment: recharges + vouchers (CSV round-trip) ---

/** Which queue to work: the two digital product categories. */
export type FulfilmentQueue = "recharges" | "vouchers";

/**
 * Download a fulfilment queue as a CSV. Returns the raw blob plus the filename the
 * API chose, so the caller can hand it straight to the browser.
 */
export async function exportFulfilmentQueue(
  queue: FulfilmentQueue,
  options: {
    status?: FulfilmentStatusFilter;
    dateFrom?: string;
    dateTo?: string;
  } = {},
): Promise<{ blob: Blob; filename: string }> {
  const params = new URLSearchParams({ status: options.status ?? "pending" });
  if (options.dateFrom) params.set("dateFrom", options.dateFrom);
  if (options.dateTo) params.set("dateTo", options.dateTo);

  const res = await authorizedFetch(`/v1/admin/${queue}/export?${params.toString()}`);
  if (!res.ok) throw await parseError(res);

  const disposition = res.headers.get("Content-Disposition") ?? "";
  const match = /filename="?([^";]+)"?/.exec(disposition);
  return { blob: await res.blob(), filename: match?.[1] ?? `${queue}.csv` };
}

/** Upload an edited export file; the `status` column is applied to each request. */
export async function importFulfilmentQueue(
  queue: FulfilmentQueue,
  file: File,
): Promise<FulfilmentImportResult> {
  const form = new FormData();
  form.append("file", file);
  return request<FulfilmentImportResult>(`/v1/admin/${queue}/import`, {
    method: "POST",
    body: form,
  });
}

// --- App users (members) ---

export async function listMembers(
  options: {
    status?: MemberStatusFilter;
    search?: string;
    field?: MemberSearchField;
    dateFrom?: string;
    dateTo?: string;
    pageNumber?: number;
    pageSize?: number;
  } = {},
): Promise<PaginatedList<Member>> {
  const {
    status = "all",
    search,
    field = "all",
    dateFrom,
    dateTo,
    pageNumber = 1,
    pageSize = DEFAULT_PAGE_SIZE,
  } = options;
  return request<PaginatedList<Member>>(
    `/v1/admin/members${buildListQuery(pageNumber, pageSize, { status, search, field, dateFrom, dateTo })}`,
  );
}

export async function getMember(id: string): Promise<Member> {
  return request<Member>(`/v1/admin/members/${id}`);
}

export async function listMemberReferrals(
  memberId: string,
  options: { pageNumber?: number; pageSize?: number } = {},
): Promise<PaginatedList<ReferredUser>> {
  const { pageNumber = 1, pageSize = DEFAULT_PAGE_SIZE } = options;
  return request<PaginatedList<ReferredUser>>(
    `/v1/admin/members/${memberId}/referrals${buildListQuery(pageNumber, pageSize)}`,
  );
}

export async function getMemberWalletHistory(
  memberId: string,
  options: { walletType: WalletTypeFilter; cursor?: number; limit?: number },
): Promise<WalletHistoryResult> {
  const { walletType, cursor, limit = 20 } = options;
  const search = new URLSearchParams({ walletType, limit: String(limit) });
  if (cursor != null) search.set("cursor", String(cursor));
  return request<WalletHistoryResult>(`/v1/admin/members/${memberId}/wallet-history?${search.toString()}`);
}

export async function updateMember(id: string, payload: UpdateMemberInput): Promise<Member> {
  return request<Member>(`/v1/admin/members/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function setMemberStatus(id: string, status: MemberStatus): Promise<void> {
  await changeMemberStatus([id], status);
}

export async function changeMemberStatus(
  ids: string[],
  status: MemberStatus,
): Promise<ChangeStatusResult> {
  return patchChangeStatus("/v1/admin/members/change-status", ids, status);
}

// --- Referrals ---

export async function listReferrals(
  options: {
    referrerSearch?: string;
    referredSearch?: string;
    dateFrom?: string;
    dateTo?: string;
    pageNumber?: number;
    pageSize?: number;
  } = {},
): Promise<PaginatedList<ReferralRecord>> {
  const {
    referrerSearch,
    referredSearch,
    dateFrom,
    dateTo,
    pageNumber = 1,
    pageSize = DEFAULT_PAGE_SIZE,
  } = options;
  return request<PaginatedList<ReferralRecord>>(
    `/v1/admin/referrals${buildListQuery(pageNumber, pageSize, {
      referrerSearch,
      referredSearch,
      dateFrom,
      dateTo,
    })}`,
  );
}

export async function getReferralStats(): Promise<ReferralStats> {
  return request<ReferralStats>("/v1/admin/referrals/stats");
}

export async function listReferralLeaderboard(
  options: { pageNumber?: number; pageSize?: number; search?: string } = {},
): Promise<PaginatedList<ReferralLeaderboardEntry>> {
  const { pageNumber = 1, pageSize = DEFAULT_PAGE_SIZE, search } = options;
  return request<PaginatedList<ReferralLeaderboardEntry>>(
    `/v1/admin/referrals/leaderboard${buildListQuery(pageNumber, pageSize, { search })}`,
  );
}

// --- Leaderboard (SP periodic snapshot / KP lifetime balance) ---

export async function listSpLeaderboard(
  options: {
    board?: SpLeaderboardBoard;
    windowKey?: string;
    search?: string;
    pageNumber?: number;
    pageSize?: number;
  } = {},
): Promise<AdminSpLeaderboardResult> {
  const { board = "weekly", windowKey, search, pageNumber = 1, pageSize = DEFAULT_PAGE_SIZE } = options;
  return request<AdminSpLeaderboardResult>(
    `/v1/admin/leaderboard/sp${buildListQuery(pageNumber, pageSize, { board, windowKey, search })}`,
  );
}

export async function listKpLeaderboard(
  options: { search?: string; pageNumber?: number; pageSize?: number } = {},
): Promise<PaginatedList<AdminKpLeaderboardRow>> {
  const { search, pageNumber = 1, pageSize = DEFAULT_PAGE_SIZE } = options;
  return request<PaginatedList<AdminKpLeaderboardRow>>(
    `/v1/admin/leaderboard/kp${buildListQuery(pageNumber, pageSize, { search })}`,
  );
}

// --- Economy config (app_config) ---

export async function listConfig(): Promise<ConfigEntry[]> {
  const res = await request<{ entries: ConfigEntry[] }>("/v1/admin/config");
  return res.entries;
}

export async function updateConfig(
  key: string,
  value: unknown,
  description?: string,
): Promise<ConfigEntry> {
  return request<ConfigEntry>(`/v1/admin/config/${encodeURIComponent(key)}`, {
    method: "PUT",
    body: JSON.stringify(description === undefined ? { value } : { value, description }),
  });
}

// --- Feedback ---

export async function listFeedback(
  options: {
    status?: FeedbackAdminStatusFilter;
    requestType?: FeedbackRequestTypeFilter;
    targetType?: FeedbackTargetTypeFilter;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
    pageNumber?: number;
    pageSize?: number;
  } = {},
): Promise<PaginatedList<FeedbackSubmission>> {
  const {
    status = "all",
    requestType = "all",
    targetType = "all",
    search,
    dateFrom,
    dateTo,
    pageNumber = 1,
    pageSize = DEFAULT_PAGE_SIZE,
  } = options;
  return request<PaginatedList<FeedbackSubmission>>(
    `/v1/admin/feedback${buildListQuery(pageNumber, pageSize, {
      status,
      requestType,
      targetType,
      search,
      dateFrom,
      dateTo,
    })}`,
  );
}

export async function getFeedback(id: string): Promise<FeedbackSubmissionDetail> {
  return request<FeedbackSubmissionDetail>(`/v1/admin/feedback/${id}`);
}

export async function replyToFeedback(
  id: string,
  message: string,
): Promise<{ queued: boolean; sendId: string | null }> {
  return request<{ queued: boolean; sendId: string | null }>(`/v1/admin/feedback/${id}/reply`, {
    method: "POST",
    body: JSON.stringify({ message }),
  });
}

export async function changeFeedbackStatus(
  ids: string[],
  status: FeedbackAdminStatus,
): Promise<ChangeStatusResult> {
  return patchChangeStatus("/v1/admin/feedback/change-status", ids, status);
}

// --- Devices ---

export async function listDevices(
  options: {
    status?: DeviceStatusFilter;
    search?: string;
    pageNumber?: number;
    pageSize?: number;
  } = {},
): Promise<PaginatedList<DeviceRecord>> {
  const { status = "all", search, pageNumber = 1, pageSize = DEFAULT_PAGE_SIZE } = options;
  return request<PaginatedList<DeviceRecord>>(
    `/v1/admin/devices${buildListQuery(pageNumber, pageSize, { status, search })}`,
  );
}

export async function blockDevice(
  id: string,
): Promise<{ id: string; deviceId: string; status: DeviceStatus }> {
  return request<{ id: string; deviceId: string; status: DeviceStatus }>(
    `/v1/admin/devices/${id}/block`,
    { method: "POST" },
  );
}

// --- AskAI ---

export async function listAskAiQuestions(
  options: {
    status?: AskAiQuestionStatusFilter;
    askerType?: AskAiAskerTypeFilter;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
    pageNumber?: number;
    pageSize?: number;
  } = {},
): Promise<PaginatedList<AskAiQuestion>> {
  const {
    status = "all",
    askerType = "all",
    search,
    dateFrom,
    dateTo,
    pageNumber = 1,
    pageSize = DEFAULT_PAGE_SIZE,
  } = options;
  return request<PaginatedList<AskAiQuestion>>(
    `/v1/admin/askai${buildListQuery(pageNumber, pageSize, {
      status,
      askerType,
      search,
      dateFrom,
      dateTo,
    })}`,
  );
}

export async function getAskAiQuestion(id: string): Promise<AskAiQuestionDetail> {
  return request<AskAiQuestionDetail>(`/v1/admin/askai/${id}`);
}

// --- Predictions ---

export async function changeAdminStatus(
  ids: string[],
  status: AdminStatus,
): Promise<ChangeStatusResult> {
  return patchChangeStatus("/v1/admin/users/change-status", ids, status);
}

export { API_URL };

// --- Feed (admin read-only, protected by admin JWT) ---

export type FeedMatchStatus = "present" | "past" | "future";

// ─── In-memory TTL cache ──────────────────────────────────────────────────────
// Keyed by request path. Each entry stores the resolved value + an expiry time.
// Ideal TTLs:
//   present (live) matches  → 30 s  — scoreboards change every ball
//   future  (upcoming) list → 3 min — schedules rarely shift
//   past    (results) list  → 10 min — results are final
//   match summary/scorecard → 45 s  — live scoring; 10 min after result
//   player profile          → 1 h  — biographical data almost never changes

const _cache = new Map<string, { data: unknown; expiresAt: number }>();

function cached<T>(key: string, ttlMs: number, fetcher: () => Promise<T>): Promise<T> {
  const hit = _cache.get(key);
  if (hit && Date.now() < hit.expiresAt) return Promise.resolve(hit.data as T);
  return fetcher().then((data) => {
    _cache.set(key, { data, expiresAt: Date.now() + ttlMs });
    return data;
  });
}

// ─────────────────────────────────────────────────────────────────────────────

/** List matches by lifecycle bucket. */
export async function listFeedMatches(
  status: FeedMatchStatus,
): Promise<FeedMatchCard[]> {
  const ttl = status === "present" ? 30_000 : status === "future" ? 180_000 : 600_000;
  return cached(`feed:matches:${status}`, ttl, () =>
    request<FeedMatchCard[]>(`/v1/admin/feed/matches?status=${status}`),
  );
}

/**
 * Full match detail (card + header + scorecards + playing XI + live).
 * Heavy — prefer getFeedMatchSummary + getFeedScorecard in parallel when possible.
 */
export async function getFeedMatch(
  matchId: string | number,
): Promise<FeedMatchDetail> {
  return cached(`feed:match:${matchId}`, 45_000, () =>
    request<FeedMatchDetail>(`/v1/admin/feed/matches/${matchId}`),
  );
}

/**
 * Lightweight match overview: card + header extras + per-innings totals + live block.
 * Fast — no batting/bowling arrays. Use for the score hero and match info panel.
 */
export async function getFeedMatchSummary(
  matchId: string | number,
): Promise<FeedMatchSummary> {
  return cached(`feed:summary:${matchId}`, 45_000, () =>
    request<FeedMatchSummary>(`/v1/admin/feed/summary/${matchId}`),
  );
}

/**
 * Full per-innings scorecards + playing XI, minimal header only.
 * Use for the scorecard and lineup tabs. Pair with getFeedMatchSummary.
 */
export async function getFeedScorecard(
  matchId: string | number,
): Promise<FeedMatchScorecard> {
  return cached(`feed:scorecard:${matchId}`, 45_000, () =>
    request<FeedMatchScorecard>(`/v1/admin/feed/scorecard/${matchId}`),
  );
}

/** All known series/competitions, most-recent first. */
export async function listFeedSeries(): Promise<FeedSeries[]> {
  return cached("feed:series", 300_000, () =>
    request<FeedSeries[]>(`/v1/admin/feed/competitions`),
  );
}

export async function getFeedSeries(cid: string | number): Promise<FeedSeriesDetail> {
  return cached(`feed:series:${cid}`, 60_000, () =>
    request<FeedSeriesDetail>(`/v1/admin/feed/competitions/${cid}`),
  );
}

/** Player profile (name, country, role, birthdate, photo). */
export async function searchFeedPlayers(
  q: string,
  limit = 25,
): Promise<FeedPlayerSearchItem[]> {
  const query = q.trim();
  if (query.length < 2) return [];
  return request<FeedPlayerSearchItem[]>(
    `/v1/admin/feed/players?q=${encodeURIComponent(query)}&limit=${limit}`,
  );
}

export async function getFeedPlayer(
  pid: string | number,
): Promise<FeedPlayerProfile> {
  return cached(`feed:player:${pid}`, 3_600_000, () =>
    request<FeedPlayerProfile>(`/v1/admin/feed/players/${pid}`),
  );
}

/** Team profile (title, abbr, type, country, logo). */
export async function getFeedTeam(tid: string | number): Promise<FeedTeamProfile> {
  return cached(`feed:team:${tid}`, 3_600_000, () =>
    request<FeedTeamProfile>(`/v1/admin/feed/teams/${tid}`),
  );
}

/** Every ingested match a team plays in, newest first (card + venue). */
export async function listFeedTeamMatches(
  tid: string | number,
): Promise<FeedScheduleMatch[]> {
  return cached(`feed:team:${tid}:matches`, 180_000, () =>
    request<FeedScheduleMatch[]>(`/v1/admin/feed/teams/${tid}/matches`),
  );
}

/** Every ingested match a player appears in (squad/playing-XI), newest first. */
export async function listFeedPlayerMatches(
  pid: string | number,
): Promise<FeedScheduleMatch[]> {
  return cached(`feed:player:${pid}:matches`, 180_000, () =>
    request<FeedScheduleMatch[]>(`/v1/admin/feed/players/${pid}/matches`),
  );
}

/**
 * Fetch a provider image through the authenticated same-origin proxy and return
 * an object URL the canvas can sample without CORS taint (the provider CDNs send
 * no CORS headers). Caller must URL.revokeObjectURL() when done.
 */
export async function fetchFeedImageObjectUrl(url: string): Promise<string> {
  const res = await authorizedFetch(`/v1/admin/feed/img?url=${encodeURIComponent(url)}`);
  if (!res.ok) throw await parseError(res);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export async function listPredictions(
  options: {
    status?: PredictionStatus;
    category?: PredictionCategoryFilter;
    matchId?: number;
    teamId?: number;
    navDestination?: string;
    pageNumber?: number;
    pageSize?: number;
  } = {},
): Promise<PaginatedList<PredictionListItem>> {
  const {
    category = "all",
    pageNumber = 1,
    pageSize = DEFAULT_PAGE_SIZE,
    status,
    matchId,
    teamId,
    navDestination,
  } = options;
  return request<PaginatedList<PredictionListItem>>(
    `/v1/admin/predictions${buildListQuery(pageNumber, pageSize, {
      status,
      matchId,
      teamId,
      navDestination,
      category: category === "all" ? undefined : category,
    })}`,
  );
}

export async function getPredictionStats(): Promise<PredictionStats> {
  return request<PredictionStats>("/v1/admin/predictions/stats");
}

export async function listRecentPredictionActivity(
  options: { pageNumber?: number; pageSize?: number } = {},
): Promise<PaginatedList<PredictionRecentActivity>> {
  const { pageNumber = 1, pageSize = 5 } = options;
  return request<PaginatedList<PredictionRecentActivity>>(
    `/v1/admin/predictions/recent-activity${buildListQuery(pageNumber, pageSize)}`,
  );
}

export async function getPrediction(id: string): Promise<PredictionDetail> {
  return request<PredictionDetail>(`/v1/admin/predictions/${id}`);
}

export async function listPredictionEntries(
  predictionId: string,
  options: {
    status?: PredictionEntryStatus | "all";
    search?: string;
    pageNumber?: number;
    pageSize?: number;
  } = {},
): Promise<PaginatedList<PredictionEntryAdmin>> {
  const { status = "all", search, pageNumber = 1, pageSize = 5 } = options;
  return request<PaginatedList<PredictionEntryAdmin>>(
    `/v1/admin/predictions/${predictionId}/entries${buildListQuery(pageNumber, pageSize, {
      status: status === "all" ? undefined : status,
      search,
    })}`,
  );
}

export async function createPrediction(payload: CreatePredictionInput): Promise<PredictionDetail> {
  return request<PredictionDetail>("/v1/admin/predictions", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updatePrediction(
  id: string,
  payload: UpdatePredictionInput,
): Promise<PredictionDetail> {
  return request<PredictionDetail>(`/v1/admin/predictions/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function declarePredictionResult(
  id: string,
  payload: DeclarePredictionResultInput,
): Promise<DeclarePredictionResult> {
  return request<DeclarePredictionResult>(`/v1/admin/predictions/${id}/declare-result`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function voidPrediction(
  id: string,
  payload: { reason: string },
): Promise<VoidPredictionResult> {
  return request<VoidPredictionResult>(`/v1/admin/predictions/${id}/void`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function deletePrediction(id: string): Promise<DeletePredictionResult> {
  return request<DeletePredictionResult>(`/v1/admin/predictions/${id}/delete`, {
    method: "POST",
  });
}

async function patchChangeStatus(
  path: string,
  ids: string[],
  status: string,
): Promise<ChangeStatusResult> {
  return request<ChangeStatusResult>(path, {
    method: "PATCH",
    body: JSON.stringify({ ids, status }),
  });
}
